// S28 AID 清冊(/aid/inventory)— 以 ca-manager 為主(B)。inventory 瀏覽/搜尋/
// 下鑽。後端 = restful 白名單代理 /v3/admin/aids* → ca-manager。
// 不顯示私鑰。AID revoke 只建立 durable maker/checker 操作，由不同
// 管理員密碼 step-up 後才呼叫 device-ca；瀏覽器永遠拿不到 device-ca key。
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import {
  listAids,
  getAid,
  getDeviceCaAid,
  getDeviceCaAidLineage,
  aidStatusLabel,
  aidStatusTone,
  type Aid,
  type DeviceCaAid,
} from './aidApi';
import { requestCommerceOperation } from '../commerce/commerceApi';
import { CommerceApprovalPanel } from '../commerce/CommerceApprovalPanel';
import { DUAL_CONTROL_NOTE } from '../commerce/commerceModel';
import '../_shared/shared.css';
import './aid.css';

const STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'available', label: '可用(在池)' },
  { value: 'allocated', label: '已配給' },
  { value: 'paired', label: '已配對' },
];

const PAGE_SIZE = 25;

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}

function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('zh-TW', { hour12: false });
}

function isNotFound(err: unknown): boolean { return err instanceof ApiError && err.status === 404; }

export default function AidInventoryPage() {
  const [fType, setFType] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fQ, setFQ] = useState('');

  const [items, setItems] = useState<Aid[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // keyset 分頁:cursorStack[i] = 載入第 (i+1) 頁所用的 cursor(index 0 = undefined = 第一頁)。
  // keyset 只能循序前後、不能跳任意頁,故用 cursor 堆疊支援「上一頁」。頁長 = PAGE_SIZE。
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [selected, setSelected] = useState<Aid | null>(null);
  const [detailErr, setDetailErr] = useState<unknown>(null);
  const [binding, setBinding] = useState<DeviceCaAid | null>(null);
  const [lineage, setLineage] = useState<DeviceCaAid[]>([]);
  const [bindingErr, setBindingErr] = useState<unknown>(null);
  const [bindingLoading, setBindingLoading] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeCaseId, setRevokeCaseId] = useState('');
  const [revokeOperationId, setRevokeOperationId] = useState('');
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState('');
  const selectionRequest = useRef(0);

  // 載入某一頁:以 cursor 取該頁、以 stack 紀錄到此頁的 cursor 路徑;結果「替換」而非追加。
  const load = useCallback(
    async (cursor: string | undefined, stack: (string | undefined)[]) => {
      setLoading(true);
      setErr(null);
      try {
        const page = await listAids({
          type: fType.trim() || undefined,
          status: fStatus || undefined,
          q: fQ.trim() || undefined,
          cursor,
          limit: PAGE_SIZE,
        });
        setItems(page.items);
        setNextCursor(page.nextCursor);
        setCursorStack(stack);
      } catch (e) {
        setErr(e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [fType, fStatus, fQ],
  );

  // 篩選變更 / 首次載入 → 回到第一頁
  const applyFilters = useCallback(() => void load(undefined, [undefined]), [load]);
  const goNext = useCallback(() => {
    if (nextCursor) void load(nextCursor, [...cursorStack, nextCursor]);
  }, [nextCursor, cursorStack, load]);
  const goPrev = useCallback(() => {
    if (cursorStack.length > 1) {
      const s = cursorStack.slice(0, -1);
      void load(s[s.length - 1], s);
    }
  }, [cursorStack, load]);

  useEffect(() => {
    void load(undefined, [undefined]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectRow = useCallback(async (row: Aid) => {
    const request = ++selectionRequest.current;
    setSelected(row);
    setDetailErr(null);
    setBinding(null);
    setLineage([]);
    setBindingErr(null);
    setBindingLoading(true);
    setRevokeOperationId('');
    setRevokeError('');
    const [detailResult, bindingResult] = await Promise.allSettled([getAid(row.did), getDeviceCaAid(row.did)]);
    if (selectionRequest.current !== request) return;
    if (detailResult.status === 'fulfilled') {
      setSelected(detailResult.value);
    } else {
      setDetailErr(detailResult.reason); // 保留清單值,明細取用失敗僅提示
    }
    if (bindingResult.status === 'rejected') {
      setBindingErr(bindingResult.reason);
      setBindingLoading(false);
      return;
    }
    setBinding(bindingResult.value);
    try {
      const items = await getDeviceCaAidLineage(bindingResult.value.aid_id);
      if (selectionRequest.current === request) setLineage(items);
    } catch (e) {
      if (selectionRequest.current === request) setBindingErr(e);
    } finally {
      if (selectionRequest.current === request) setBindingLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    selectionRequest.current += 1;
    setSelected(null);
    setBinding(null);
    setLineage([]);
    setBindingErr(null);
    setBindingLoading(false);
    setRevokeOperationId('');
    setRevokeError('');
  }, []);

  const requestAidRevoke = useCallback(async () => {
    if (!binding || !revokeReason.trim() || !revokeCaseId.trim()) return;
    setRevokeBusy(true);
    setRevokeError('');
    try {
      const operation = await requestCommerceOperation(
        'aid.revoke',
        { aid_id: binding.aid_id },
        { reason: revokeReason.trim(), case_id: revokeCaseId.trim() },
      );
      setRevokeOperationId(operation.operation_id);
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : '建立 AID revoke 覆核操作失敗');
    } finally {
      setRevokeBusy(false);
    }
  }, [binding, revokeReason, revokeCaseId]);

  return (
    <div className="aid-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">AID 清冊</h1>
          <p className="page-subtitle">
            AID(裝置身分憑證)inventory 瀏覽、單筆下鑽。不顯示私鑰。後端 = ca-manager(restful 代理{' '}
            <span className="adm-mono">/v3/admin/aids*</span>)。
          </p>
        </div>
      </div>

      <div className="aid-split">
        <section className="card">
          <div className="section-head">
            <div>
              <h2 className="section-title">清冊</h2>
              <span className="section-meta">依 AID 類型 / 狀態 / DID 前綴篩選</span>
            </div>
            {loading && <span className="pill pill-neutral">查詢中</span>}
          </div>

          <div className="aid-filter-row">
            <label className="adm-field">
              <span className="adm-field-label text-secondary">AID 類型</span>
              <input
                value={fType}
                onChange={(e) => setFType(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="CAM"
                maxLength={3}
              />
            </label>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">狀態</span>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">DID 前綴</span>
              <input
                value={fQ}
                onChange={(e) => setFQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="CAM-…"
              />
            </label>
            <button className="btn-primary btn-sm" disabled={loading} onClick={applyFilters}>
              套用篩選
            </button>
          </div>

          {err ? (
            <ErrorState error={err} onRetry={applyFilters} />
          ) : loading && items.length === 0 ? (
            <LoadingState />
          ) : items.length > 0 ? (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>DID</th>
                      <th>AID 類型</th>
                      <th>狀態</th>
                      <th>PO／組織</th>
                      <th>MAC</th>
                      <th>有效期限</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((a) => (
                      <tr
                        key={a.did}
                        className={`clickable ${selected?.did === a.did ? 'is-selected' : ''}`}
                        onClick={() => void selectRow(a)}
                      >
                        <td className="adm-mono">{a.did}</td>
                        <td className="adm-mono">{a.type}</td>
                        <td>
                          <span className={`pill ${aidStatusTone(a.alloc_status)}`}>{aidStatusLabel(a.alloc_status)}</span>
                        </td>
                        <td className="adm-mono">
                          {a.po_number ?? '—'}
                          <div className="text-tertiary adm-mono">{a.org_id ?? ''}</div>
                        </td>
                        <td className="adm-mono">{a.mac ?? '—'}</td>
                        <td className="adm-mono">{fmtDate(a.not_after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(nextCursor || cursorStack.length > 1) && (
                <div className="aid-pager">
                  <button className="btn-ghost btn-sm" disabled={loading || cursorStack.length <= 1} onClick={goPrev}>
                    ← 上一頁
                  </button>
                  <span className="section-meta">
                    第 {cursorStack.length} 頁 · 本頁 {items.length} 筆
                  </span>
                  <button className="btn-ghost btn-sm" disabled={loading || !nextCursor} onClick={goNext}>
                    下一頁 →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🔖</div>
              <div className="empty-state-title">沒有符合篩選的 AID</div>
              <div className="empty-state-detail">調整 type / 狀態 / DID 前綴篩選。</div>
            </div>
          )}
        </section>

        <aside className="aid-side">
          {selected ? (
            <section className="card">
              <div className="section-head">
                <div>
                  <h2 className="section-title">AID 明細</h2>
                  <span className="section-meta adm-mono">{selected.did}</span>
                </div>
                <button className="btn-ghost btn-sm" onClick={closeDetail}>
                  關閉
                </button>
              </div>
              {detailErr != null && <ErrorState error={detailErr} />}
              <dl className="aid-kv-grid">
                <div className="aid-kv">
                  <dt className="aid-kv-label">AID 類型</dt>
                  <dd className="aid-kv-value adm-mono">{selected.type}</dd>
                </div>
                <div className="aid-kv">
                  <dt className="aid-kv-label">序號</dt>
                  <dd className="aid-kv-value adm-mono">{selected.serial}</dd>
                </div>
                <div className="aid-kv">
                  <dt className="aid-kv-label">狀態</dt>
                  <dd className="aid-kv-value">
                    <span className={`pill ${aidStatusTone(selected.alloc_status)}`}>{aidStatusLabel(selected.alloc_status)}</span>
                  </dd>
                </div>
                <div className="aid-kv">
                  <dt className="aid-kv-label">PO</dt>
                  <dd className="aid-kv-value adm-mono">{selected.po_number ?? '—'}</dd>
                </div>
                <div className="aid-kv">
                  <dt className="aid-kv-label">組織</dt>
                  <dd className="aid-kv-value adm-mono">{selected.org_id ?? '—'}</dd>
                </div>
                <div className="aid-kv">
                  <dt className="aid-kv-label">MAC</dt>
                  <dd className="aid-kv-value adm-mono">{selected.mac ?? '—'}</dd>
                </div>
                <div className="aid-kv">
                  <dt className="aid-kv-label">效期</dt>
                  <dd className="aid-kv-value adm-mono">
                    {fmtDate(selected.not_before)} → {fmtDate(selected.not_after)}
                  </dd>
                </div>
                <div className="aid-kv">
                  <dt className="aid-kv-label">匯入批次</dt>
                  <dd className="aid-kv-value adm-mono">{selected.import_id ?? '—'}</dd>
                </div>
              </dl>
              <div className="aid-lineage" data-testid="aid-activation-binding">
                <h3 className="aid-lineage-title">啟用綁定 / 換貨更替鏈</h3>
                {bindingLoading && !binding ? <p className="aid-hint">正在查詢 device-ca 權威紀錄…</p> : null}
                {bindingErr && isNotFound(bindingErr) && !binding ? (
                  <p className="aid-gap-note">此 DID 尚無 device-ca activation binding；正式 PKI 切換前不以測試資料回填。</p>
                ) : null}
                {bindingErr && !isNotFound(bindingErr) ? <ErrorState error={bindingErr} /> : null}
                {binding ? (
                  <>
                    <dl className="aid-kv-grid">
                      <div className="aid-kv">
                        <dt className="aid-kv-label">目前 AID</dt>
                        <dd className="aid-kv-value adm-mono">{binding.aid_id}</dd>
                      </div>
                      <div className="aid-kv">
                        <dt className="aid-kv-label">裝置 ID</dt>
                        <dd className="aid-kv-value adm-mono">{binding.device_id}</dd>
                      </div>
                      <div className="aid-kv">
                        <dt className="aid-kv-label">狀態 / 等級</dt>
                        <dd className="aid-kv-value">{binding.status} · {binding.aid_level_id} v{binding.aid_level_version}</dd>
                      </div>
                      <div className="aid-kv">
                        <dt className="aid-kv-label">啟用時間</dt>
                        <dd className="aid-kv-value adm-mono">{fmtDateTime(binding.activated_at)}</dd>
                      </div>
                      <div className="aid-kv">
                        <dt className="aid-kv-label">批次</dt>
                        <dd className="aid-kv-value adm-mono">{binding.batch_id ?? '—'}</dd>
                      </div>
                    </dl>
                    <ol className="aid-lineage-list" aria-label="換貨更替鏈">
                      {(lineage.length > 0 ? lineage : [binding]).map((item, index, items) => (
                        <li key={item.aid_id} className={item.aid_id === binding.aid_id ? 'is-current' : ''}>
                          <div className="aid-lineage-step">
                            <span className="aid-lineage-index">{index + 1}</span>
                            <div>
                              <strong className="adm-mono">{item.aid_id}</strong>
                              <div className="text-secondary adm-mono">{item.device_id}</div>
                              <div className="text-tertiary">
                                {item.status} · activated {fmtDateTime(item.activated_at)}
                                {index === items.length - 1 ? ' · newest' : ''}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                    <div className="aid-lineage">
                      <h3 className="aid-lineage-title">撤銷目前 AID</h3>
                      <p className="commerce-dc-note">{DUAL_CONTROL_NOTE} 撤銷後裝置須依維修／RMA 流程取得新 AID。</p>
                      <div className="aid-filter-row">
                        <label className="adm-field">
                          <span className="adm-field-label text-secondary">理由</span>
                          <input value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} placeholder="例如：憑證疑似外洩" />
                        </label>
                        <label className="adm-field">
                          <span className="adm-field-label text-secondary">案件編號</span>
                          <input value={revokeCaseId} onChange={(e) => setRevokeCaseId(e.target.value)} placeholder="SEC-…" />
                        </label>
                        <button
                          className="btn-danger btn-sm"
                          disabled={revokeBusy || !revokeReason.trim() || !revokeCaseId.trim() || binding.status === 'revoked'}
                          onClick={() => void requestAidRevoke()}
                        >
                          建立撤銷覆核
                        </button>
                      </div>
                      {revokeError && <p className="adm-action-result is-err" role="alert">{revokeError}</p>}
                    </div>
                  </>
                ) : null}
              </div>
              {revokeOperationId && (
                <CommerceApprovalPanel
                  operationId={revokeOperationId}
                  onCompleted={() => selected && void selectRow(selected)}
                />
              )}
              <p className="aid-hint">不顯示私鑰(供應鏈鐵則);憑證交付走裝置端配對流程。</p>
            </section>
          ) : (
            <section className="card">
              <p className="aid-hint">點選左側一列查看 AID 明細(AID 類型 / 序號 / 狀態 / PO / 有效期 / 來源批次)。</p>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
